import { isImagePlaceholder, type SceneDocument, type SceneElement } from "@cva/shared";
import { Check, FolderOpen, Upload, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MediaPickerDialog } from "../../components/media-picker";
import { cn } from "../../lib/utils";
import { replaceImage } from "../commands";
import { toAssetInfo, useEnv } from "../env";
import { useEditor, useEditorApi } from "../store";
import { addClipFromAsset, SPEAKER_SLOT, videoIntoDevice, videoIntoPlaceholder } from "./commands";
import { useVideoApi } from "./store";

/**
 * "Sprecher & App in zwei Schritten": for video templates with a phone
 * mockup, a small card on the canvas that puts the speaker's video where it
 * belongs (main track, or the template's "Sprecher" placeholder) and the
 * screen recording into the device – from the computer or the media library.
 * It shows the steps the document had when it was opened and disappears once
 * they are done (or when closed; remembered per document).
 */

type Step = "speaker" | "screen";

function pageElements(doc: SceneDocument): SceneElement[] {
  const result: SceneElement[] = [];
  const visit = (ids: readonly string[]) => {
    for (const id of ids) {
      const element = doc.elements[id];
      if (!element || element.hidden) continue;
      if (element.type === "group") visit(element.children);
      else result.push(element);
    }
  };
  visit(doc.pages[0]?.children ?? []);
  return result;
}

function targets(doc: SceneDocument) {
  const elements = pageElements(doc);
  const device = elements.find(
    (element) => element.type === "device" && !element.assetId && !element.locked,
  );
  const slot = elements.find(
    (element) => isImagePlaceholder(element) && element.name === SPEAKER_SLOT && !element.locked,
  );
  const onVideo = doc.pages[0]?.background === "#000000";
  const mainClips = doc.timeline?.clips.length ?? 0;
  return { device, slot, onVideo, mainClips };
}

const dismissKey = (documentId: string) => `cva:speaker-setup:${documentId}`;

export function SpeakerSetup() {
  const store = useEditorApi();
  const video = useVideoApi();
  const env = useEnv();
  const doc = useEditor((s) => s.doc);
  const readOnly = useEditor((s) => s.readOnly);
  const { device, slot, mainClips } = targets(doc);
  // The steps the document offered when it was opened (a template with a phone, maybe a speaker slot).
  const [start] = useState(() => {
    const at = targets(store.getState().doc);
    const steps: Step[] = [];
    if (at.device && (at.slot || (at.onVideo && at.mainClips === 0))) steps.push("speaker");
    if (at.device) steps.push("screen");
    return { steps, inSlot: Boolean(at.slot) };
  });
  const steps = start.steps;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(dismissKey(env.documentId)) === "1";
    } catch {
      return false;
    }
  });
  const [pending, setPending] = useState<Record<Step, boolean>>({ speaker: false, screen: false });
  const [picking, setPicking] = useState<Step | null>(null);
  const speakerFile = useRef<HTMLInputElement>(null);
  const screenFile = useRef<HTMLInputElement>(null);

  // In a slot the speaker step is done once the placeholder became a clip, else once the main track has one.
  const done: Record<Step, boolean> = {
    speaker: start.inSlot ? !slot : mainClips > 0,
    screen: !device,
  };
  useEffect(() => {
    setPending((current) => ({
      speaker: current.speaker && !done.speaker,
      screen: current.screen && !done.screen,
    }));
  }, [done.speaker, done.screen]);

  if (!video || readOnly || !env.canEdit || dismissed || steps.length === 0) return null;
  if (steps.every((step) => done[step])) return null;

  const close = () => {
    setDismissed(true);
    try {
      localStorage.setItem(dismissKey(env.documentId), "1");
    } catch {
      // Private mode: the card only stays closed for this visit.
    }
  };

  const upload = (step: Step, files: FileList | null) => {
    const list = [...(files ?? [])].slice(0, 1);
    if (list.length === 0) return;
    video.getState().seek(0);
    setPending((current) => ({ ...current, [step]: true }));
    if (step === "speaker") {
      env.uploadAndInsert(list, slot ? { replaceId: slot.id } : { timeline: { index: 0 } });
    } else if (device) {
      env.uploadAndInsert(list, { replaceId: device.id });
    }
  };

  const labels: Record<Step, { title: string; hint: string }> = {
    speaker: {
      title: "Dein Video",
      hint: slot
        ? `Du vor der Kamera – läuft im ${slot.type === "shape" && slot.shape === "ellipse" ? "Kreis" : "Rahmen"} der Vorlage.`
        : "Du vor der Kamera – liegt als Hauptvideo unter der Vorlage.",
    },
    screen: {
      title: "Bildschirmaufnahme",
      hint: "Läuft im Handy der Vorlage. Ein Screenshot geht auch.",
    },
  };

  return (
    <div className="absolute top-3 left-3 z-20 w-64 rounded-xl border border-zinc-200 bg-white/95 p-3 shadow-lg backdrop-blur">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-zinc-900">
            {steps.length > 1 ? "Sprecher & App in 2 Schritten" : "Bildschirmaufnahme ins Gerät"}
          </p>
          <p className="text-xs text-zinc-500">Die Vorlage legt alles an die richtige Stelle.</p>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Schließen"
          className="rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
        >
          <X className="size-4" />
        </button>
      </div>
      <ol className="space-y-2">
        {steps.map((step, index) => {
          const finished = done[step];
          const busy = pending[step] && !finished;
          return (
            <li
              key={step}
              className={cn(
                "rounded-lg border px-2.5 py-2",
                finished ? "border-emerald-200 bg-emerald-50/60" : "border-zinc-200",
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
                    finished ? "bg-emerald-600 text-white" : "bg-zinc-900 text-white",
                  )}
                >
                  {finished ? <Check className="size-3" /> : index + 1}
                </span>
                <span className="text-sm font-medium text-zinc-800">{labels[step].title}</span>
              </div>
              {finished ? null : (
                <>
                  <p className="mt-1 text-xs text-zinc-500">
                    {busy ? "Wird hochgeladen und verarbeitet …" : labels[step].hint}
                  </p>
                  <div className="mt-2 flex gap-1.5">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        (step === "speaker" ? speakerFile : screenFile).current?.click()
                      }
                      className="inline-flex h-7 flex-1 items-center justify-center gap-1 rounded-md bg-brand-600 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                    >
                      <Upload className="size-3.5" /> Hochladen
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setPicking(step)}
                      className="inline-flex h-7 flex-1 items-center justify-center gap-1 rounded-md border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                    >
                      <FolderOpen className="size-3.5" /> Mediathek
                    </button>
                  </div>
                </>
              )}
            </li>
          );
        })}
      </ol>
      <input
        ref={speakerFile}
        type="file"
        data-speaker-setup="speaker"
        accept="video/*"
        className="hidden"
        onChange={(event) => {
          upload("speaker", event.target.files);
          event.target.value = "";
        }}
      />
      <input
        ref={screenFile}
        type="file"
        data-speaker-setup="screen"
        accept="video/*,image/*"
        className="hidden"
        onChange={(event) => {
          upload("screen", event.target.files);
          event.target.value = "";
        }}
      />
      {picking ? (
        <MediaPickerDialog
          orgId={env.orgId}
          open
          onOpenChange={(open) => (open ? null : setPicking(null))}
          title={picking === "speaker" ? "Dein Video wählen" : "Bildschirmaufnahme wählen"}
          description={labels[picking].hint}
          kinds={picking === "speaker" ? ["video"] : ["video", "image"]}
          onPick={([asset]) => {
            const step = picking;
            setPicking(null);
            if (!asset) return;
            video.getState().seek(0);
            const info = toAssetInfo(asset);
            if (step === "speaker") {
              if (asset.kind !== "video") return;
              if (slot) videoIntoPlaceholder(store, video, slot.id, info);
              else addClipFromAsset(store, video, info, { index: 0 });
            } else if (device) {
              if (asset.kind === "video") videoIntoDevice(store, video, device.id, info);
              else if (asset.kind === "image") replaceImage(store, device.id, asset.id, asset);
            }
          }}
        />
      ) : null}
    </div>
  );
}
